// /finance's To do (finance redesign slice 3, DD-041; on jobs, finance consolidation S9): only the steps that move
// money, in the order it moves, each group with its footer total, the server's (TD-102). To invoice: one row per client
// with a draft invoice or done jobs, at all its drafts' totals and those jobs' prices, opening
// its one draft (`modal=job-invoice`), the client's drawer when it has several drafts, or New invoice over the client's
// drawer with those jobs ticked (`modal=client&stack=new-invoice&pick=`); the agency's own client is never invoiced
// (TD-105), so its done jobs, and those past their last day, are rows to close until every claim on them is on a team invoice (answer c), each opening
// its job drawer, and add to no total. Waiting for payment: sent and partly paid invoices with money still due, overdue
// first, each opening its invoice drawer. Team to pay: one row per person with a team invoice to pay or owed claims on
// none, opening their team invoice (the draft, else the received one), or their drawer with only owed claims; the total
// is the Team tab's Owed now. Bills to pay are main's. Fix before sending: a job on a draft, done or past its period that
// nobody claims on, a job over its cap, each opening its job drawer, and a cost passed along that names no client,
// which no Send carries (TD-100), opening its expense or bill. Each row is the button for its step, which opens in a
// drawer on this page (DD-051), so a row names no action (finance-real round 3). An invoice row leads with whose it is
// and the invoice's name ("Mister Coffee · Invoice INV2600048", "Agustina · Draft team invoice") over its date line and
// count (invoiceDateLine), and a status is the shared read-only pill (StatusPill): Waiting's Sent, Overdue or Partly
// paid, Team to pay's Draft or Received. A job row is the job's name over its subline (jobSubline, the client first: To
// do lists every client's jobs) and its note. Fix before sending has no money total, so its footer counts its rows.
import type { ReactNode } from "react";
import { Badge } from "@/components/ui/Badge";
import { MoneyText } from "@/components/ui/MoneyText";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import { WorkspacePanel } from "@/domains/Ideas/components/WorkspacePanel";
import { formatMoney } from "@/utils/currency";
import { TabPending } from "../components/TabPending";
import { TotalsFooter } from "../components/TotalsFooter";
import { JOB_INVOICE_STATES, TEAM_INVOICE_STATES } from "../clientPage/lineJobText";
import { StatusPill } from "../clientPage/StatusBadge";
import type { ParamPatch } from "../hooks/useFinanceParams";
import type { BillRow, FixKind, FixRow, JobFixRow, TeamToPayRow, ToInvoiceRow, WaitingInvoiceRow } from "../types";
import { dateLabel } from "../utils/format";
import { AGENCY, ageText, claimsCount, invoiceDateLine, invoiceName, jobsCount, jobSubline, withCount } from "../utils/names";
import { useToDoTab } from "./hooks/useToDoTab";

// The user's job, a line item, rides main's `contract` key (the entityConfig shadow).
const JOB = ENTITY_CONFIG.contract;
const jobWord = JOB.label.toLowerCase();
const INVOICE = ENTITY_CONFIG.invoice;
const invoiceWord = INVOICE.label.toLowerCase();
const clientWord = ENTITY_CONFIG.client.label.toLowerCase();
const TEAM = ENTITY_CONFIG.teamInvoice;
const money = (minor: number): string => formatMoney(minor, "EUR");
const jobsText = jobsCount;
const claimsText = claimsCount;

/** Where a cost waiting for a job opens (J23, main's row): its expense or its bill; without either, its client. */
export function fixTarget(row: FixRow): ParamPatch {
  if (row.kind === "pass_contract" && row.templateId) return { modal: "expense-item", tpl: row.templateId };
  if (row.kind === "pass_contract" && row.billId) return { modal: "expense", entry: row.billId };
  return { modal: "client", client: row.clientId };
}

/** Main's fix kinds: only a cost passed along that names no client is a row now (`pass_contract`, kept from main's wire):
 * a cost rides its client's next Send (TD-100). Nobody doing a month and an expired quote were the contract months'. */
const COST_FIX: Record<FixKind, { badge: string; note: (row: FixRow) => string }> = {
  no_shares: { badge: "Nobody does it", note: (r) => `Nobody does ${r.subject.toLowerCase()}.` },
  pass_contract: { badge: `Pick a ${clientWord}`, note: (r) => `${r.subject} names no ${clientWord}, so no ${invoiceWord} carries it.` },
  quote_expired: { badge: "Expired", note: (r) => `${r.subject} expired ${dateLabel(r.date)}.` },
};

/** A bill with no amount yet that the total counts at its usual one. Its note takes Cash's words (cashText costNote). */
const countsUsual = (b: BillRow): boolean => b.missing && b.countedMinor !== 0;

function billNote(b: BillRow): string {
  const due = `Due ${dateLabel(b.date)}.`;
  if (!b.missing) return `${due} Mark it paid when it leaves the account.`;
  return countsUsual(b) ? `${due} No amount yet, counted at its usual.` : `${due} Enter what it came to.`;
}

const DRAFT_INVOICE = invoiceName({ kind: "client", status: "draft", number: null, sentOn: null });

/** A draft's row: its newest draft's date line and count, as every invoice reads (invoiceDateLine), and with several
 * drafts that draft's own total of how many, since the amount holds them all; then its done jobs on none. */
function toInvoiceNote(r: ToInvoiceRow): string {
  const ended = r.ended.length > 0 ? `${jobsText(r.ended.length)} done, on no ${invoiceWord}: ${r.ended.map((j) => j.label).join(" · ")}` : null;
  if (!r.draft) return ended ?? "";
  const opened = withCount(invoiceDateLine({ kind: "client", status: "draft", createdOn: r.draft.createdOn, sentOn: null, paidOn: null }), jobsText(r.draft.jobCount));
  const draft = r.draftCount > 1 ? `${opened}, ${money(r.draft.invoiceMinor)} of ${r.draftCount} drafts` : opened;
  return ended ? `${draft}. ${ended}` : draft;
}

/** Where a To invoice row opens: its one draft; with several, the client's drawer, which lists them all; with none, New
 * invoice over the client's drawer with the server's done jobs ticked (`pick=`). */
export function toInvoiceTarget(r: ToInvoiceRow): ParamPatch {
  if (r.draft && r.draftCount <= 1) return { modal: "job-invoice", invoice: r.draft.id };
  if (r.draft) return { modal: "client", client: r.clientId };
  return { modal: "client", client: r.clientId, stack: "new-invoice", pick: r.ended.map((j) => j.jobId).join(",") };
}

/** "Overdue · sent 18 Sep 2026 · 3 jobs · 33 days": the date line, the count, then its age. */
const waitingNote = (r: WaitingInvoiceRow): string =>
  `${withCount(invoiceDateLine({ kind: "client", status: r.status, createdOn: r.sentOn, sentOn: r.sentOn, paidOn: null }), jobsText(r.jobCount))} · ${ageText(r.days)}`;

/** The team invoice the row opens: its draft, else its received one. */
function payOpened(r: TeamToPayRow): { status: "draft" | "received"; createdOn: string; receivedOn: string | null; claimCount: number } | null {
  if (r.draft) return { status: "draft", createdOn: r.draft.createdOn, receivedOn: null, claimCount: r.draft.claimCount };
  if (r.received) return { status: "received", ...r.received };
  return null;
}

/** "Agustina · Draft team invoice": whose it is, then the invoice it opens; with none, the person. */
function payTail(r: TeamToPayRow): string | null {
  const opened = payOpened(r);
  return opened ? invoiceName({ kind: "team", ...opened }) : null;
}

/** The opened invoice's date line and count, then a received one waiting beside a draft, then owed claims on none. */
function payNote(r: TeamToPayRow): string {
  const opened = payOpened(r);
  const parts = [
    ...(opened ? [withCount(invoiceDateLine({ kind: "team", ...opened, paidOn: null }), claimsText(opened.claimCount))] : []),
    ...(r.draft && r.received ? [`${invoiceName({ kind: "team", status: "received", receivedOn: r.received.receivedOn })}, ${money(r.received.totalMinor)}`] : []),
    ...(r.openClaimCount > 0 ? [`${claimsText(r.openClaimCount)} owed on no ${TEAM.label.toLowerCase()}, ${money(r.openMinor)}`] : []),
  ];
  return parts.join(" · ");
}

/** The team invoice the row opens, by its status pill; with none yet, the step. */
function payBadge(r: TeamToPayRow): ReactNode {
  const opened = payOpened(r);
  return opened ? <StatusPill look={TEAM_INVOICE_STATES[opened.status]} /> : <Badge tone="amber">No {TEAM.label.toLowerCase()} yet</Badge>;
}

function jobFixNote(r: JobFixRow): string {
  if (r.kind === "over_cap") return `Its claims pass its cap by ${money(r.overMinor)}: lower a claim or raise its price.`;
  return r.onDraft ? `On a draft ${invoiceWord}, and nobody claims on it.` : "It is done or its period has ended, and nobody claims on it.";
}

/** A group with no money total counts its rows in the footer instead ("2 to fix"), in TotalsFooter's look, so every group
 * has one (DD-041). */
function CountFooter({ count, label }: { count: number; label: string }) {
  return (
    <div role="group" aria-label="Totals" className="flex flex-wrap items-center justify-end gap-x-6 gap-y-1 bg-gray-50 px-4 py-3 text-sm">
      <span className="text-gray-500">
        <span className="font-semibold text-gray-900">{count}</span>
        {` ${label}`}
      </span>
    </div>
  );
}

/** One group (J22): a WorkspacePanel with the row count beside its title and the server's total in the shared footer
 * (finance-real round 3), under the label that names it; a group with no money total, its count. */
function Group({ title, hint, count, total, countLabel, children }: { title: string; hint: string; count: number; total?: { label: string; amountMinor: number }; countLabel?: string; children: ReactNode }) {
  const footer = count === 0 ? undefined : total !== undefined ? <TotalsFooter items={[total]} /> : countLabel ? <CountFooter count={count} label={countLabel} /> : undefined;
  return (
    <WorkspacePanel
      title={title}
      summary={count}
      collapsed={false}
      footer={footer}
      bodyClassName="p-0"
    >
      {count === 0 ? <p className="px-4 pb-4 text-sm text-gray-400">{hint}</p> : <ul className="divide-y divide-gray-100">{children}</ul>}
    </WorkspacePanel>
  );
}

interface RowProps {
  /** Whose it is: the client or the person. */
  title: string;
  /** After the title and a " · ": the invoice's name (invoiceName). */
  tail?: string | null;
  /** A job row's subline, every job row's (jobSubline: client · period · how it bills), over the note. */
  sub?: string;
  note: string;
  amountMinor: number | null;
  /** Gray: a figure the total counts that is not the real one yet. */
  muted?: boolean;
  badge?: ReactNode;
  onClick: () => void;
}

/** One step: the whole row is the button. Its action is in the drawer it opens (finance-real round 3). Its name and
 * subline wrap, never cut, and the pill and amount keep their own column on the right; on a phone the pill sits over the
 * amount, so name, status and amount stay in view (DD-055), as InvoiceListRow's do. */
function Row({ title, tail = null, sub, note, amountMinor, muted, badge, onClick }: RowProps) {
  return (
    <li>
      <button type="button" onClick={onClick} className="w-full flex items-center gap-3 sm:gap-4 px-4 py-3 text-left hover:bg-gray-50">
        <span className="min-w-0 flex-1">
          <span className="block text-sm text-gray-900 [overflow-wrap:anywhere]">{tail ? `${title} · ${tail}` : title}</span>
          {sub && <span className="block text-xs text-gray-500 [overflow-wrap:anywhere]">{sub}</span>}
          {note && <span className="block text-xs text-gray-500 [overflow-wrap:anywhere]">{note}</span>}
        </span>
        <span className="flex shrink-0 flex-col items-end gap-1 sm:flex-row sm:items-center sm:gap-4">
          {badge}
          {amountMinor === null ? (
            <span className="hidden sm:block sm:w-28 text-right text-gray-400">·</span>
          ) : (
            <MoneyText amountMinor={amountMinor} muted={muted} className="text-sm sm:w-28 text-right" />
          )}
        </span>
      </button>
    </li>
  );
}

const openJob = (jobId: string): ParamPatch => ({ modal: "line-job", lineJob: jobId });

export function ToDoTab({ onOpen }: { onOpen: (patch: ParamPatch) => void }) {
  const query = useToDoTab();
  const data = query.data;
  if (!data) {
    return (
      <div className="flex-1 min-h-0 overflow-auto px-4 lg:px-6 pb-6 space-y-4">
        <TabPending failed={query.isError} message="Could not load what needs doing." retrying={query.isFetching} onRetry={() => void query.refetch()} />
      </div>
    );
  }
  const { toInvoice, toClose, waiting, toPay, bills, fix } = data;
  return (
    <div className="flex-1 min-h-0 overflow-auto px-4 lg:px-6 pb-6 space-y-4">
      <Group
        title={`To ${invoiceWord}`}
        hint={`Nothing to ${invoiceWord}: every done ${jobWord} is on an ${invoiceWord}.`}
        count={toInvoice.rows.length + toClose.length}
        // Rows to close alone move no money (TD-105): the footer counts them instead of a total of nothing.
        total={toInvoice.rows.length > 0 ? { label: "To invoice", amountMinor: toInvoice.totalMinor } : undefined}
        countLabel="to close"
      >
        {toInvoice.rows.map((r) => (
          <Row
            key={r.clientId}
            title={r.clientName}
            tail={r.draft ? DRAFT_INVOICE : jobsText(r.ended.length)}
            note={toInvoiceNote(r)}
            amountMinor={r.amountMinor}
            badge={<Badge tone="amber">{r.draft ? "To send" : `To ${invoiceWord}`}</Badge>}
            onClick={() => onOpen(toInvoiceTarget(r))}
          />
        ))}
        {/* The own client's work is never invoiced (TD-105): what it costs is in its note, out of the total. */}
        {toClose.map((r) => (
          <Row
            key={r.jobId}
            title={r.name}
            sub={jobSubline(r, AGENCY)}
            note={`The agency's own ${jobWord} is done or its period has ended: it is never invoiced, and its claims cost ${money(r.costMinor)}.`}
            amountMinor={null}
            badge={<Badge tone="amber">To close</Badge>}
            onClick={() => onOpen(openJob(r.jobId))}
          />
        ))}
      </Group>

      <Group title="Waiting for payment" hint={`No ${invoiceWord} is waiting.`} count={waiting.rows.length} total={{ label: "Still due", amountMinor: waiting.totalMinor }}>
        {waiting.rows.map((r) => (
          <Row
            key={r.invoiceId}
            title={r.clientName}
            tail={invoiceName({ kind: "client", status: r.status, number: r.number, sentOn: r.sentOn })}
            note={waitingNote(r)}
            amountMinor={r.dueMinor}
            badge={<StatusPill look={JOB_INVOICE_STATES[r.status]} />}
            onClick={() => onOpen({ modal: "job-invoice", invoice: r.invoiceId })}
          />
        ))}
      </Group>

      {/* DD-054: the Team tab's basis and label, Owed now; Cash counts work done to today, under its own label. */}
      <Group title="Team to pay" hint="Nobody is owed anything." count={toPay.rows.length} total={{ label: "Owed now", amountMinor: toPay.totalMinor }}>
        {toPay.rows.map((r) => (
          <Row
            key={r.personId}
            title={r.name}
            tail={payTail(r)}
            note={payNote(r)}
            amountMinor={r.owedMinor}
            badge={r.isOwner ? <Badge tone="indigo">Owner</Badge> : payBadge(r)}
            onClick={() => onOpen(r.opens.kind === "team-invoice" ? { modal: "team-invoice", teamInvoice: r.opens.teamInvoiceId } : { modal: "person", person: r.personId })}
          />
        ))}
      </Group>

      <Group title="Bills to pay" hint="Every bill that fell due is paid." count={bills.rows.length} total={{ label: "Total", amountMinor: bills.totalMinor }}>
        {bills.rows.map((r) => (
          <Row
            key={r.billId}
            title={r.clientName ? `${r.label} · ${r.clientName}` : r.label}
            note={billNote(r)}
            amountMinor={countsUsual(r) ? r.countedMinor : r.amountMinor}
            muted={r.missing}
            badge={<Badge tone="amber">{r.missing ? "No amount" : "To pay"}</Badge>}
            onClick={() => onOpen({ modal: "expense", entry: r.billId })}
          />
        ))}
      </Group>

      <Group title="Fix before sending" hint={`Nothing holds an ${invoiceWord} up.`} count={fix.jobs.length + fix.costs.length} countLabel="to fix">
        {fix.jobs.map((r) => (
          <Row
            key={r.key}
            title={r.name}
            sub={jobSubline(r, r.clientName)}
            note={jobFixNote(r)}
            amountMinor={null}
            badge={<Badge tone="amber">{r.kind === "over_cap" ? "Over the cap" : "Nobody claims"}</Badge>}
            onClick={() => onOpen(openJob(r.jobId))}
          />
        ))}
        {fix.costs.map((r) => (
          <Row
            key={r.key}
            title={r.clientName ? `${r.clientName} · ${r.subject}` : r.subject}
            note={COST_FIX[r.kind].note(r)}
            amountMinor={null}
            badge={<Badge tone="amber">{COST_FIX[r.kind].badge}</Badge>}
            onClick={() => onOpen(fixTarget(r))}
          />
        ))}
      </Group>
    </div>
  );
}
